if (!window.intertecStoreAvailabilityInit) {
  window.intertecStoreAvailabilityInit = true;

  const sectionStates = new Map();

  function getState(sectionId) {
    if (!sectionStates.has(sectionId)) {
      sectionStates.set(sectionId, {
        controller: null,
        requestToken: 0,
        requestedVariantId: null,
        selectedVariantId: null,
      });
    }

    return sectionStates.get(sectionId);
  }

  function getRefs(sectionId) {
    const modal = document.getElementById(`StoreAvailabilityModal-${sectionId}`);
    const body = modal?.querySelector('[data-store-availability-body]');

    return modal && body ? { modal, body } : null;
  }

  function getCurrentVariantId(sectionId) {
    const productForm = document.getElementById(`product-form-${sectionId}`);
    const variantInput = productForm?.querySelector('input[name="id"]');

    if (variantInput?.value) return String(variantInput.value);

    // B2B products intentionally do not render the buy form. The theme's
    // variant picker still exposes the selected variant synchronously.
    const variantPicker =
      document.getElementById(`variant-radios-${sectionId}`) ||
      document.getElementById(`variant-selects-${sectionId}`);
    const pickerVariantId = variantPicker?.currentVariant?.id;

    return pickerVariantId ? String(pickerVariantId) : null;
  }

  function getChangedSectionId(target) {
    if (!target?.closest) return null;

    const variantPicker = target.closest('variant-radios[data-section], variant-selects[data-section]');
    if (variantPicker) return variantPicker.dataset.section || null;

    if (!target.matches('input.product-variant-id[name="id"]')) return null;

    return target.closest('product-form[data-section-id]')?.dataset.sectionId || null;
  }

  function clearNotice(body) {
    body.querySelectorAll('.store-availability-loading, .store-availability-error').forEach((notice) => {
      notice.remove();
    });
  }

  function hideRenderedAvailability(body, hidden) {
    body.querySelectorAll('.store-availability-list, .store-availability-empty').forEach((content) => {
      content.toggleAttribute('hidden', hidden);
      content.classList.toggle('hidden', hidden);
    });
  }

  function showLoading(body) {
    clearNotice(body);
    hideRenderedAvailability(body, true);
    body.setAttribute('aria-busy', 'true');

    const notice = document.createElement('p');
    notice.className = 'store-availability-loading';
    notice.setAttribute('role', 'status');
    notice.textContent = 'Updating store availability…';
    body.prepend(notice);
  }

  function showError(body) {
    clearNotice(body);
    hideRenderedAvailability(body, true);
    body.removeAttribute('aria-busy');

    const notice = document.createElement('p');
    notice.className = 'store-availability-error';
    notice.setAttribute('role', 'alert');
    notice.textContent = 'Store availability could not be refreshed. Please try again.';
    body.prepend(notice);
  }

  function showRenderedAvailability(body) {
    clearNotice(body);
    hideRenderedAvailability(body, false);
    body.removeAttribute('aria-busy');
  }

  function invalidateRequest(state) {
    // Aborting saves work; changing the token is what makes a late response
    // harmless even if the transport has already progressed past the abort.
    state.requestToken += 1;
    state.controller?.abort();
    state.controller = null;
    state.requestedVariantId = null;
  }

  function isCurrentRequest(state, requestToken, controller, sectionId, variantId) {
    return (
      state.requestToken === requestToken &&
      state.controller === controller &&
      state.selectedVariantId === variantId &&
      getCurrentVariantId(sectionId) === variantId
    );
  }

  function buildSectionUrl(productUrl, sectionId, variantId) {
    const url = new URL(productUrl, window.location.origin);
    const currentUrl = new URL(window.location.href);
    const currentPreviewThemeId = currentUrl.searchParams.get('preview_theme_id');
    const themeRole = window.Shopify?.theme?.role;
    const isThemePreview = window.Shopify?.designMode || (themeRole && themeRole !== 'main');
    const previewThemeId = currentPreviewThemeId || (isThemePreview ? window.Shopify?.theme?.id : null);

    url.searchParams.set('variant', variantId);
    // Request the real main-product section that owns this modal. Shopify's
    // variant picker already refreshes this same section ID successfully.
    // A hard-coded static section ID can return 404 in theme previews.
    url.searchParams.set('section_id', sectionId);
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

    return url.toString();
  }

  function refreshStoreAvailability(sectionId, variantId) {
    const normalizedVariantId = variantId ? String(variantId) : null;
    const refs = getRefs(sectionId);
    if (!refs || !normalizedVariantId) return;

    const { modal, body } = refs;
    const state = getState(sectionId);
    state.selectedVariantId = normalizedVariantId;

    if (body.dataset.renderedVariantId === normalizedVariantId) {
      invalidateRequest(state);
      showRenderedAvailability(body);
      return;
    }

    if (state.controller && state.requestedVariantId === normalizedVariantId) return;

    const productUrl = modal.dataset.productUrl;
    if (!productUrl) return;

    invalidateRequest(state);

    let requestUrl;
    try {
      requestUrl = buildSectionUrl(productUrl, sectionId, normalizedVariantId);
    } catch (error) {
      console.error('[StoreAvailability] Could not build the refresh URL:', error);
      showError(body);
      return;
    }

    const controller = new AbortController();
    const requestToken = state.requestToken;
    state.controller = controller;
    state.requestedVariantId = normalizedVariantId;

    showLoading(body);

    fetch(requestUrl, {
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Store availability request failed with status ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (!isCurrentRequest(state, requestToken, controller, sectionId, normalizedVariantId)) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newBody = html.querySelector('[data-store-availability-body]');

        if (!newBody) throw new Error('Store availability response is missing its body');
        if (newBody.dataset.renderedVariantId !== normalizedVariantId) {
          const returnedVariantId = newBody.dataset.renderedVariantId || 'unknown';
          throw new Error(
            `Store availability response returned variant ${returnedVariantId} instead of ${normalizedVariantId}`
          );
        }

        if (!isCurrentRequest(state, requestToken, controller, sectionId, normalizedVariantId)) return;

        const currentBody = getRefs(sectionId)?.body;
        if (!currentBody) return;

        currentBody.replaceWith(newBody);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        if (!isCurrentRequest(state, requestToken, controller, sectionId, normalizedVariantId)) return;

        console.error('[StoreAvailability] Refresh failed:', error);

        const currentBody = getRefs(sectionId)?.body;
        if (currentBody) showError(currentBody);
      })
      .finally(() => {
        if (state.requestToken !== requestToken || state.controller !== controller) return;

        state.controller = null;
        state.requestedVariantId = null;

        const currentBody = getRefs(sectionId)?.body;
        if (currentBody?.dataset.renderedVariantId === normalizedVariantId) {
          showRenderedAvailability(currentBody);
          return;
        }

        const liveVariantId = getCurrentVariantId(sectionId);
        const currentRefs = getRefs(sectionId);
        if (liveVariantId && liveVariantId !== normalizedVariantId && currentRefs?.modal.hasAttribute('open')) {
          refreshStoreAvailability(sectionId, liveVariantId);
        }
      });
  }

  function handleVariantChange(sectionId) {
    const variantId = getCurrentVariantId(sectionId);
    if (!variantId) return;

    const state = getState(sectionId);
    if (state.selectedVariantId === variantId) return;

    state.selectedVariantId = variantId;
    invalidateRequest(state);

    const refs = getRefs(sectionId);
    if (!refs) return;

    if (refs.body.dataset.renderedVariantId === variantId) {
      showRenderedAvailability(refs.body);
      return;
    }

    if (refs.modal.hasAttribute('open')) refreshStoreAvailability(sectionId, variantId);
  }

  document.addEventListener('change', (event) => {
    const sectionId = getChangedSectionId(event.target);
    if (sectionId) handleVariantChange(sectionId);
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-store-availability-trigger]');
    if (!trigger) return;

    const sectionId = trigger.dataset.sectionId;
    const variantId = getCurrentVariantId(sectionId);
    if (sectionId && variantId) refreshStoreAvailability(sectionId, variantId);
  });
}
